import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Chart, ChartConfiguration } from "chart.js/auto"
import "chartjs-adapter-date-fns"
import { useSelector } from "react-redux"
import { selectUser } from "../../utils/redux/userSlice"
import { getGraphData } from "../../utils/services/data.service"
import { ICharData, ICharOptions } from "aw-graph"

type Point = { x: string; y: number }

const pad2 = (n: number) => n.toString().padStart(2, "0")
const normDate = (s: string) => {
    const [y, m, d] = s.split("/").map(Number)
    return `${y}/${pad2(m)}/${pad2(d)}`
}
const toNum = (v: unknown) => {
    if (v == null) return 0
    if (typeof v === "number") return Number.isFinite(v) ? v : 0
    const s = String(v).trim()
    if (!s) return 0
    const n = Number(s.replace(/\./g, "").replace(",", "."))
    return Number.isFinite(n) ? n : 0
}

function useChartData(
    userToken: string,
    startDate: string,
    endDate: string,
    options: ICharOptions[]
) {
    const [data, setData] = useState<ICharData[]>([])
    const [error, setError] = useState<unknown>(null)

    useEffect(() => {
        let alive = true
        getGraphData(userToken, startDate, endDate, JSON.stringify(options))
            .then((d) => {
                if (alive) setData(d ?? [])
            })
            .catch((e) => {
                if (alive) setError(e)
            })
        return () => {
            alive = false
        }
    }, [userToken, startDate, endDate, options])

    return { data, error }
}

/**
 * useLineChart
 * - Carrega dades (getGraphData)
 * - Les transforma a {x,y}
 * - Renderitza un Chart.js en un canvas offscreen
 * - Retorna el dataURL (string) quan està llest
 */
export function useLineChart() {
    const user = useSelector(selectUser)
    const { t } = useTranslation()

    const year = 2025
    const month = 7
    const monthStr = String(month).padStart(2, "0")
    const startDate = `${year}-${monthStr}-01`
    const endDate = `${year}-${monthStr}-30`

    const options: ICharOptions[] = useMemo(
        () => [
            {
                label: t("Profit"),
                key: "Mesure 1",
                color: "#82ca9d",
                units: "€",
                minValue: 0,
                maxValue: 1400,
                value: 0
            },
            {
                label: t("Losses"),
                key: "Mesure 2",
                color: "#CF594D",
                units: "€",
                minValue: 0,
                maxValue: 1050,
                value: 1
            }
        ],
        [t]
    )

    const { data } = useChartData(user.token, startDate, endDate, options)

    const { beneficis, perdues } = useMemo(() => {
        const sorted = [...data].sort((a, b) => {
            const A = a.xData.split("/").map(Number)
            const B = b.xData.split("/").map(Number)
            return (
                A[0] * 10000 +
                A[1] * 100 +
                A[2] -
                (B[0] * 10000 + B[1] * 100 + B[2])
            )
        })
        return {
            beneficis: sorted.map((r) => ({
                x: normDate(r.xData),
                y: toNum(r["Mesure 1"])
            })),
            perdues: sorted.map((r) => ({
                x: normDate(r.xData),
                y: toNum(r["Mesure 2"])
            }))
        }
    }, [data])

    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const [img, setImg] = useState<string>()

    // Crear el canvas offscreen una sola vegada
    if (!canvasRef.current) {
        const c = document.createElement("canvas")
        c.width = 800
        c.height = 300
        canvasRef.current = c
    }

    useEffect(() => {
        if (!canvasRef.current) return
        const ctx = canvasRef.current.getContext("2d")!
        const cfg: ChartConfiguration<"line", Point[], unknown> = {
            type: "line",
            data: {
                datasets: [
                    {
                        label: t("Profit"),
                        data: beneficis,
                        borderColor: "#82CA9D",
                        backgroundColor: "#82CA9D",
                        borderJoinStyle: "round",
                        pointRadius: 0,
                        tension: 0.4,
                        borderWidth: 2
                    },
                    {
                        label: t("Losses"),
                        data: perdues,
                        borderColor: "#CF594D",
                        backgroundColor: "#CF594D",
                        borderJoinStyle: "round",
                        pointRadius: 0,
                        tension: 0.4,
                        borderWidth: 2
                    }
                ]
            },
            options: {
                responsive: false,
                animation: false,
                plugins: {
                    legend: {
                        position: "top",
                        align: "end",
                        labels: {
                            boxWidth: 12,
                            boxHeight: 12,
                            padding: 20,
                            borderRadius: 0,
                            usePointStyle: true,
                            pointStyle: "rect"
                        }
                    }
                },
                parsing: { xAxisKey: "x", yAxisKey: "y" },
                scales: {
                    x: {
                        type: "time",
                        time: {
                            parser: "yyyy/MM/dd",
                            unit: "day",
                            displayFormats: {
                                day: "yyyy/M/d"
                            }
                        },
                        ticks: {
                            autoSkip: true,
                            maxTicksLimit: 10
                        },
                        border: {
                            dash: [5, 5]
                        }
                    },
                    y: {
                        beginAtZero: true,
                        border: {
                            dash: [5, 5]
                        }
                    }
                }
            }
        }

        const chart = new Chart(ctx, cfg)
        setImg(canvasRef.current.toDataURL("image/png"))
        return () => chart.destroy()
    }, [beneficis, perdues, t])
    return img
}

type AlarmType = "Minor" | "Warning" | "Critical" | "No Alarms"
type AlarmData = { name: AlarmType; value: number }

export function usePieChartImage(
    alarmData: AlarmData[],
    colors: Record<AlarmType, string>,
    opts?: { width?: number; height?: number; doughnut?: boolean }
) {
    const { width = 500, height = 500, doughnut = false } = opts ?? {}
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const [img, setImg] = useState<string>()

    if (!canvasRef.current) {
        const c = document.createElement("canvas")
        c.width = width
        c.height = height
        canvasRef.current = c
    }

    const { labels, values, bgColors } = useMemo(() => {
        const labels = alarmData.map((d) => d.name)
        const values = alarmData.map((d) => d.value)
        const bgColors = alarmData.map((d) => colors[d.name])
        return { labels, values, bgColors }
    }, [JSON.stringify(alarmData), JSON.stringify(colors)])

    useEffect(() => {
        if (!canvasRef.current) return
        const ctx = canvasRef.current.getContext("2d")!
        const cfg: ChartConfiguration<"pie", number[], string> = {
            type: "pie",
            data: {
                labels,
                datasets: [
                    {
                        data: values,
                        backgroundColor: bgColors,
                        borderColor: "#fff",
                        borderWidth: 0,

                        borderJoinStyle: "round",

                        offset: 0
                    }
                ]
            },
            options: {
                responsive: false,
                animation: false,
                plugins: {
                    legend: {
                        position: "bottom",
                        labels: {
                            boxWidth: 12,
                            boxHeight: 12,
                            padding: 20,
                            borderRadius: 0,
                            borderWidth: 0,
                            usePointStyle: true,
                            pointStyle: "rect",
                            borderColor:"red"
                        },

                        tooltip: { enabled: true }
                    }
                },

                cutout: doughnut ? "50%" : 0
            } as any
        }

        const chart = new Chart(ctx, cfg)
        setImg(canvasRef.current.toDataURL("image/png"))
        return () => chart.destroy()
    }, [labels, values, bgColors, doughnut, width, height])

    return img
}

export type BarPoint = number
export type BarSeries = {
    label: string
    data: BarPoint[]
    backgroundColor?: string | string[]
    borderColor?: string | string[]
}

type UseBarOpts = {
    width?: number
    height?: number
    horizontal?: boolean
    stacked?: boolean
    maxBarThickness?: number
    rounded?: boolean
    gridColor?: string
    showLegend?: boolean
}

export function useBarChartImage(
    labels: string[],
    series: BarSeries[],
    opts: UseBarOpts = {}
) {
    const {
        width = 900,
        height = 400,
        horizontal = false,
        stacked = false,
        maxBarThickness = 28,
        rounded = true,
        gridColor = "#e5e7eb",
        showLegend = false
    } = opts

    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const [img, setImg] = useState<string>()

    if (!canvasRef.current) {
        const c = document.createElement("canvas")
        c.width = width
        c.height = height
        canvasRef.current = c
    }

    const depsKey = useMemo(
        () => JSON.stringify({ labels, series, horizontal, stacked, rounded }),
        [labels, series, horizontal, stacked, rounded]
    )

    useEffect(() => {
        if (!canvasRef.current) return
        const ctx = canvasRef.current.getContext("2d")!

        const cfg: ChartConfiguration<"bar", number[], string> = {
            type: "bar",
            data: {
                labels,
                datasets: series.map((s) => ({
                    label: s.label,
                    data: s.data,
                    backgroundColor: "#19DAAD",
                    borderColor: "#19DAAD",
                    maxBarThickness,
                    borderSkipped: false,
                    borderRadius: {
                        topLeft: 5,
                        topRight: 5,
                        bottomLeft: 0,
                        bottomRight: 0
                    }
                }))
            },
            options: {
                responsive: false,
                animation: false,
                indexAxis: horizontal ? "y" : "x",
                plugins: {
                    legend: { display: showLegend, position: "bottom" },
                    tooltip: { enabled: true }
                },
                scales: {
                    x: {
                        stacked,
                        grid: { color: gridColor },
                        ticks: {
                            color: "#374151",
                            maxTicksLimit: 18,
                            align: "center"
                        },
                        offset: true,
                        border: {
                            dash: [5, 5]
                        }
                    },
                    y: {
                        stacked,
                        beginAtZero: true,
                        grid: { color: gridColor },
                        ticks: { color: "#374151", count: 3 },
                        border: {
                            dash: [5, 5]
                        }
                    }
                }
            }
        }
        const chart = new Chart(ctx, cfg)
        setImg(canvasRef.current.toDataURL("image/png"))
        return () => chart.destroy()
    }, [depsKey, width, height, maxBarThickness, gridColor, showLegend])
    return img
}
